import BybButtonChip from "@Components/common/byb-ds/buttons/byb-button-chip";
import { BybRewardSticker } from "@Components/common/byb-ds/byb-reward-sticker";
import BybAdaptiveModal from "@Components/common/byb-ds/drawers/byb-adaptive-modal";
import { getRarityIcon } from "@Components/common/byb-ds/icons/byb-reward-icon";
import BybSectionHeader from "@Components/common/byb-ds/sections/byb-section-header";
import formatMetricScoreType from "@Containers/app/stat-cards/tooltips/format-metric-type";
import MetricEventTypes from "@Enums/MetricEventTypes";
import RewardScarcity from "@Enums/RewardScarcity";
import { formatRankWithOrdinal } from "@Helpers/format-utils";
import { BybListItemBackgroundColorGradient, BybRewardColor } from "@Helpers/rewards-mappings";
import { useRewardUserStats } from "@hooks/common/rewards/use-reward-user-stats";
import useI18n from "@i18n";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import React from "react";

interface IBybAdaptiveModal {
  drawerOpen: boolean;
  onClose: () => void;
  rewardId: number;
  challengeId: number | null | undefined;
  name: string;
  goal?: string | null;
  scarcity: RewardScarcity;
  color?: BybListItemBackgroundColorGradient;
  earnedAt?: string;
}

interface IRewardInformationSection {
  scarcity: RewardScarcity;
  color?: BybListItemBackgroundColorGradient;
  name: string;
  goal?: string | null;
  userRank: number | null;
}

interface IRewardDetailsSection {
  scarcity: RewardScarcity;
  rewardPercentage: number | null;
  earnedAt?: string;
  bestResultIq: number | null;
  bgColor: string;
  rarityIcon: JSX.Element;
  theme: any;
}

const getBgColor = (scarcity: RewardScarcity, theme: any): string => {
  const BybRewardDetailsBackgroundColor: Record<string, string> = {
    red: theme.palette.dsDestructive.v100,
    blue: theme.palette.dsPrimary.v200,
    orange: theme.palette.dsSecondaryYellow.v100,
    purple: theme.palette.dsSecondaryPurple.v200,
    green: theme.palette.dsSuccess.v100,
  };
  return BybRewardDetailsBackgroundColor[BybRewardColor[scarcity]];
};

const RewardInformationSection: React.FC<IRewardInformationSection> = ({ scarcity, color, name, goal, userRank }) => {
  const { i18n } = useI18n();

  return (
    <Stack alignItems="center" spacing={2}>
      <BybRewardSticker scarcity={scarcity} color={color} />
      <BybSectionHeader title={name} subTitle={goal} />
      {userRank === null ? (
        <BybButtonChip loading />
      ) : userRank > 0 ? (
        <BybButtonChip>{i18n("challenges.reward.place", { place: formatRankWithOrdinal(userRank, i18n) })}</BybButtonChip>
      ) : (
        <BybButtonChip>{i18n("challenges.reward.not.ranked")}</BybButtonChip>
      )}
    </Stack>
  );
};

const RewardDetailsSection: React.FC<IRewardDetailsSection> = ({
  scarcity,
  rewardPercentage,
  earnedAt,
  bestResultIq,
  bgColor,
  rarityIcon,
  theme,
}) => {
  const { i18n } = useI18n();

  return (
    <Stack
      spacing={1}
      textAlign="center"
      paddingY={theme.spacing(2.5)}
      bgcolor={bgColor}
      borderRadius={theme.spacing(2)}
    >
      <Box display="flex" alignItems="center" justifyContent="center" gap={theme.spacing(1)}>
        {rarityIcon}
        <Typography variant="textS500" sx={{ color: "dsNeutral.v300", textTransform: "capitalize" }}>
          {i18n(`challenges.reward.scarcity.${scarcity}`)}
        </Typography>
        <Typography variant="textS500" color="dsNeutral.v300">
          ·
        </Typography>
        {rewardPercentage !== null ? (
          <Typography variant="textS500" color="dsNeutral.v300">
            {i18n("challenges.reward.scarcity", { scarcity: rewardPercentage })}
          </Typography>
        ) : (
          <Skeleton variant="text" width={200} />
        )}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center" gap={theme.spacing(1)}>
        <Typography variant="textXs500" color="dsNeutral.v300">
          {!!earnedAt
            ? i18n("challenges.reward.achieved.on", { date: format(new Date(earnedAt), "dd MMM yyyy") })
            : i18n("challenges.reward.not.achieved")}
        </Typography>

        {!!earnedAt && (
          <>
            <Typography variant="textXs500" color="dsNeutral.v300">
              ·
            </Typography>

            {bestResultIq ? (
              <Typography variant="textXs500" color="dsNeutral.v300">
                {i18n("challenges.reward.best.iq", {
                  iq: formatMetricScoreType(MetricEventTypes.BYB_IQ, bestResultIq),
                })}
              </Typography>
            ) : (
              <Skeleton variant="text" width={70} />
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

const BybRewardDrawer: React.FC<IBybAdaptiveModal> = ({
  drawerOpen,
  onClose,
  rewardId,
  challengeId,
  name,
  goal,
  scarcity,
  color,
  earnedAt,
}) => {
  const theme = useTheme();
  const { rewardPercentage, userRank, bestResultIq } = useRewardUserStats(rewardId, challengeId, drawerOpen);

  const rarityIcon = getRarityIcon(scarcity, color, theme);
  const bgColor = getBgColor(scarcity, theme);

  return (
    <BybAdaptiveModal open={drawerOpen} onClose={onClose} anchor="bottom" draggable={true} sx={{ maxHeight: "90vh" }}>
      <Stack spacing={4}>
        <RewardInformationSection scarcity={scarcity} color={color} name={name} goal={goal} userRank={userRank} />
        <RewardDetailsSection
          scarcity={scarcity}
          rewardPercentage={rewardPercentage}
          earnedAt={earnedAt}
          bestResultIq={bestResultIq}
          bgColor={bgColor}
          rarityIcon={rarityIcon}
          theme={theme}
        />
      </Stack>
    </BybAdaptiveModal>
  );
};

export default BybRewardDrawer;
